import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import LoadingOverlay from '../LoadingOverlay';
import Toast from '../Toast';
import { normalizeRestaurantId } from '../../utils/restaurantUtils';

const CookAttendance = ({ cookId, restaurantId: restaurantIdProp }) => {
    const restaurantId = normalizeRestaurantId(restaurantIdProp);
    const [attendance, setAttendance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    useEffect(() => {
        if (cookId) {
            fetchTodayAttendance();
        }
    }, [cookId]);

    const fetchTodayAttendance = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_ENDPOINTS.ATTENDANCE_GET, {
                params: { 
                    staffId: cookId, 
                    date: new Date().toISOString().split('T')[0] 
                }
            });
            if (response.data && response.data.length > 0) {
                setAttendance(response.data[0]);
            }
        } catch (error) {
            console.error('Failed to fetch attendance:', error);
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const getCurrentShift = () => {
        const hour = new Date().getHours();
        if (hour >= 9 && hour < 15) return 'MORNING';
        if (hour >= 15 && hour < 22) return 'EVENING';
        return 'NIGHT';
    };

    const handleCheckIn = async (shift) => {
        setActionLoading(true);
        setLoadingMessage('Checking in...');
        try {
            const response = await axios.post(API_ENDPOINTS.ATTENDANCE_CHECKIN, {
                staffId: cookId,
                shift,
                restaurantId
            });
            setAttendance(response.data);
            showToast('Checked in successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Check-in failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCheckOut = async () => {
        if (!attendance || attendance.checkOutTime) return;
        
        setActionLoading(true);
        setLoadingMessage('Checking out...');
        try {
            const response = await axios.post(API_ENDPOINTS.ATTENDANCE_CHECKOUT, {
                staffId: cookId,
                shift: attendance.shift
            });
            setAttendance(response.data);
            showToast('Checked out successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Check-out failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading attendance...</p>
                </div>
            </div>
        );
    }

    const currentShift = getCurrentShift();
    const isCheckedIn = attendance && !attendance.checkOutTime;
    const status = isCheckedIn ? 'PRESENT' : attendance?.status || 'ABSENT';

    return (
        <div className="relative max-w-2xl mx-auto space-y-4 sm:space-y-6">
            {actionLoading && <LoadingOverlay message={loadingMessage} />}

            {/* Status Card */}
            <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl shadow-xl p-6 sm:p-8 text-white text-center">
                <div className={`inline-block px-6 py-3 rounded-full text-lg sm:text-xl font-bold mb-4 ${
                    status === 'PRESENT' ? 'bg-green-500' :
                    status === 'LATE' ? 'bg-yellow-500' :
                    'bg-red-500'
                }`}>
                    {status === 'PRESENT' ? '🟢 PRESENT' :
                     status === 'LATE' ? '🟡 LATE' :
                     '🔴 ABSENT'}
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-2">Today's Attendance</h2>
            </div>

            {/* Attendance Details Card */}
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
                <div className="space-y-4">
                    {attendance?.checkInTime && (
                        <div className="flex justify-between items-center py-3 border-b border-gray-200">
                            <span className="text-gray-600 font-medium">Check In:</span>
                            <span className="font-bold text-gray-800">
                                {new Date(attendance.checkInTime).toLocaleString()}
                            </span>
                        </div>
                    )}
                    {attendance?.checkOutTime && (
                        <div className="flex justify-between items-center py-3 border-b border-gray-200">
                            <span className="text-gray-600 font-medium">Check Out:</span>
                            <span className="font-bold text-gray-800">
                                {new Date(attendance.checkOutTime).toLocaleString()}
                            </span>
                        </div>
                    )}
                    {attendance?.totalHours > 0 && (
                        <div className="flex justify-between items-center py-3 border-b border-gray-200">
                            <span className="text-gray-600 font-medium">Total Hours:</span>
                            <span className="font-bold text-gray-800">{attendance.totalHours} hrs</span>
                        </div>
                    )}
                </div>

                {/* Action Button */}
                <div className="mt-6">
                    {!isCheckedIn ? (
                        <button
                            onClick={() => handleCheckIn(currentShift)}
                            className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl"
                        >
                            ✓ Check In Now
                        </button>
                    ) : (
                        <button
                            onClick={handleCheckOut}
                            className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:from-red-600 hover:to-red-700 transition-all shadow-lg hover:shadow-xl"
                        >
                            ✗ Check Out
                        </button>
                    )}
                </div>
            </div>

            {/* Info Box */}
            <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 sm:p-6">
                <div className="flex items-start gap-3">
                    <span className="text-2xl">ℹ️</span>
                    <div>
                        <h4 className="font-bold text-orange-900 mb-2 text-sm sm:text-base">Important Notes</h4>
                        <ul className="text-xs sm:text-sm text-orange-800 space-y-1">
                            <li>• You must be checked in to receive order assignments</li>
                            <li>• Only one active shift can be active at a time</li>
                            <li>• Attendance is automatically recorded with timestamps</li>
                            <li>• Check out when your shift ends</li>
                        </ul>
                    </div>
                </div>
            </div>

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default CookAttendance;
